import { User } from '@supabase/supabase-js';
import { fetchApi } from '../lib/apiClient';

/**
 * 邀请码相关工具函数
 */



/**
 * 验证邀请码
 * @param code 邀请码
 * @returns 验证结果
 */
export const verifyInviteCode = async (code: string): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    if (!code || typeof code !== 'string') {
      return {
        success: false,
        message: '邀请码不能为空'
      };
    }

    // 简单的格式验证
    const codePattern = /^[A-Z0-9-]+$/;
    if (!codePattern.test(code.toUpperCase())) {
      return {
        success: false,
        message: '邀请码格式无效'
      };
    }

    const data = await fetchApi<{ success: boolean; message: string; valid?: boolean }>('/api/invite/verify', {
      method: 'POST',
      body: { code: code.toUpperCase() },
    });

    return {
      success: data.success || false,
      message: data.message || '验证成功'
    };
  } catch (error) {
    console.error('[InviteCodeUtils] Error verifying invite code:', error);
    return {
      success: false,
      message: '网络错误，请重试'
    };
  }
};

/**
 * 格式化邀请码输入
 * @param input 用户输入
 * @returns 格式化后的邀请码
 */
export const formatInviteCode = (input: string): string => {
  // 转换为大写，移除空格和特殊字符（除了连字符）
  return input
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '')
    .trim();
};

/**
 * 验证邀请码格式
 * @param code 邀请码
 * @returns 是否格式正确
 */
export const isValidInviteCodeFormat = (code: string): boolean => {
  if (!code || typeof code !== 'string') {
    return false;
  }

  // 基本格式检查：只允许大写字母、数字和连字符
  const pattern = /^[A-Z0-9-]+$/;
  return pattern.test(code) && code.length >= 3 && code.length <= 20;
};

/**
 * 获取邀请码错误提示
 * @param error 错误信息
 * @returns 用户友好的错误提示
 */
export const getInviteCodeErrorMessage = (error: string): string => {
  const errorMap: { [key: string]: string } = {
    '邀请码不存在或已失效': '邀请码不存在或已失效，请检查输入是否正确',
    '邀请码已被使用': '此邀请码已被使用，请联系管理员获取新的邀请码',
    '您已经使用过邀请码': '您已经验证过邀请码，无需重复验证',
    '邀请码不能为空': '请输入邀请码',
    '邀请码格式无效': '邀请码格式不正确，请检查输入',
    '验证失败，请重试': '验证失败，请稍后重试',
    '网络错误，请重试': '网络连接异常，请检查网络后重试',
  };

  return errorMap[error] || error || '未知错误，请重试';
};

/**
 * 保存邀请码重定向路径
 * @param path 要保存的路径
 */
export const setInviteCodeRedirectPath = (path: string): void => {
  try {
    localStorage.setItem('invite-code-redirect-path', path);
  } catch (e) {
    console.warn('[InviteCodeUtils] Failed to save redirect path:', e);
  }
};

/**
 * 获取并清除邀请码重定向路径
 * @returns 保存的重定向路径
 */
export const getAndClearInviteCodeRedirectPath = (): string | null => {
  try {
    const path = localStorage.getItem('invite-code-redirect-path');
    if (path) {
      localStorage.removeItem('invite-code-redirect-path');
    }
    return path;
  } catch (e) {
    console.warn('[InviteCodeUtils] Failed to get redirect path:', e);
    return null;
  }
};

/**
 * 获取邀请码验证完成后的跳转路径
 * @param intendedPath 用户原本想要访问的路径
 * @returns 验证完成后的跳转路径
 */
export const getPostInviteCodeVerificationPath = (intendedPath?: string | null): string => {
  // 如果有指定的路径，使用指定路径
  if (intendedPath && intendedPath !== '/v3/invite-code') {
    return intendedPath;
  }

  // 默认跳转到主页
  return '/v3/taskv2';
};
